import React from 'react';
import { Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  FaApple,
  FaInstagram,
  FaXTwitter,
  FaLinkedinIn,
  FaFacebookF,
  FaTiktok,
  FaYoutube,
  FaSnapchat,
  FaPhone,
  FaEnvelope,
  FaLocationDot,
} from 'react-icons/fa6';
import logo from '../../../assets/images/logo.svg';
import Icon from '../../ui/Icon';
import { Reveal } from '../../motion';
import { CONTACT_EMAIL, MAIN_PHONE, SOCIAL_LINKS, telHref } from '../../../constants/contact';
import styles from './Footer.module.css';

const GooglePlayIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="22"
    height="24"
    viewBox="0 0 60 65"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M0.65 1.5 C0.25 2 0 2.7 0 3.5 v58 c0 0.8 0.25 1.5 0.65 2 L33.07 32.5 0.65 1.5 Z"
      fill="#00C3FF"
    />
    <path
      d="M44.07 21.5 L6.1 0.45 C4 -0.5 2 -0.3 0.65 1 L33.07 32.5 44.07 21.5 Z"
      fill="#00DE7A"
    />
    <path
      d="M44.07 43.5 L33.07 32.5 44.07 21.5 L57.5 29.1 c3.5 2 3.5 5.3 0 7.3 L44.07 43.5 Z"
      fill="#FFCE00"
    />
    <path
      d="M0.65 64 C2 65.3 4 65.5 6.1 64.55 L44.07 43.5 33.07 32.5 0.65 64 Z"
      fill="#FF3A44"
    />
  </svg>
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SOCIAL_ICONS: Record<string, any> = {
  instagram: FaInstagram,
  x: FaXTwitter,
  linkedin: FaLinkedinIn,
  facebook: FaFacebookF,
  tiktok: FaTiktok,
  youtube: FaYoutube,
  snapchat: FaSnapchat,
};

const SERVICE_LINKS = [
  { label: 'Courier Services', to: '/services/courier' },
  { label: 'Airport to Airport', to: '/services/airport' },
  { label: 'Door to Door Cargo', to: '/services/door-to-door' },
  { label: 'Packing & Shipping', to: '/services/packing-and-shipping' },
];

const COMPANY_LINKS = [
  { label: 'About us', to: '/about' },
  { label: 'Tracking', to: '/tracking' },
  { label: 'Book a shipment', to: '/book-shipment' },
  { label: 'Contact us', to: '/contact' },
];

/**
 * Site footer: a red "Ready to ship?" band, then the dark footer — brand and
 * social icons, services, company links, contact details — and a legal bar.
 */
const Footer: React.FC = () => {
  const year = new Date().getFullYear();

  return (
    <footer className={styles.footer}>
      {/* ── Red call-to-action band ── */}
      <div className={styles.band}>
        <Container fluid="xl" className={`${styles.inner} ${styles.bandInner}`}>
          <div>
            <p className={styles.bandTitle}>Ready to ship?</p>
            <p className={styles.bandText}>
              Tell us what you're sending and where — we'll quote air and sea.
            </p>
          </div>
          <div className={styles.bandButtons}>
            <Link to="/book-shipment" className={styles.bandPrimary}>
              Get a quote
            </Link>
            <Link to="/tracking" className={styles.bandGhost}>
              Track shipment
            </Link>
          </div>
        </Container>
      </div>

      {/* ── Dark footer ── */}
      <div className={styles.main}>
        <Container fluid="xl" className={styles.inner}>
          <div className={styles.grid}>
            <Reveal className={styles.brand}>
              <img src={logo} alt="Sahari Cargo" className={styles.logo} />
              <p className={styles.blurb}>
                Cargo &amp; courier from Saudi Arabia — 12 branches, 10 countries,
                customs handled.
              </p>
              <p className={styles.follow}>Follow us</p>
              <ul className={styles.socials}>
                {SOCIAL_LINKS.map((s) => {
                  const icon = <Icon icon={SOCIAL_ICONS[s.key]} aria-hidden="true" />;
                  return (
                    <li key={s.key}>
                      {s.url ? (
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.socialBtn}
                          aria-label={`Sahari Cargo on ${s.label}`}
                        >
                          {icon}
                        </a>
                      ) : (
                        // No profile link yet — show the icon without a dead link.
                        <span className={styles.socialBtn} title={s.label} aria-label={s.label}>
                          {icon}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Reveal>

            <Reveal delay={0.08}>
              <h2 className={styles.heading}>Our Services</h2>
              <ul className={styles.list}>
                {SERVICE_LINKS.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className={styles.link}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.12}>
              <h2 className={styles.heading}>Company</h2>
              <ul className={styles.list}>
                {COMPANY_LINKS.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className={styles.link}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.16} className={styles.contactCol}>
              <h2 className={styles.heading}>Contact</h2>
              <ul className={`${styles.list} ${styles.contact}`}>
                <li>
                  <Icon icon={FaPhone} className={styles.contactIcon} aria-hidden="true" />
                  <a href={telHref(MAIN_PHONE)} className={styles.link}>
                    {MAIN_PHONE}
                  </a>
                </li>
                <li>
                  <Icon icon={FaEnvelope} className={styles.contactIcon} aria-hidden="true" />
                  <a href={`mailto:${CONTACT_EMAIL}`} className={styles.link}>
                    {CONTACT_EMAIL}
                  </a>
                </li>
                <li>
                  <Icon icon={FaLocationDot} className={styles.contactIcon} aria-hidden="true" />
                  <span>
                    Head office: Riyadh, Saudi Arabia
                    <br />
                    12 branches across KSA
                  </span>
                </li>
              </ul>
              <div className={styles.appBadges}>
                <span className={styles.badge} aria-label="Download on the App Store">
                  <Icon icon={FaApple} className={styles.badgeIcon} />
                  <span className={styles.badgeText}>
                    <small>Download on the</small>
                    <strong>App Store</strong>
                  </span>
                </span>
                <span className={styles.badge} aria-label="Get it on Google Play">
                  <GooglePlayIcon className={styles.badgeIcon} />
                  <span className={styles.badgeText}>
                    <small>Get it on</small>
                    <strong>Google Play</strong>
                  </span>
                </span>
              </div>
            </Reveal>
          </div>

          <div className={styles.bottom}>
            <p className={styles.copy}>&copy; {year} Sahari Cargo. All rights reserved.</p>
            <div className={styles.legalLinks}>
              <Link to="/terms">Terms &amp; Conditions</Link>
              <Link to="/privacy">Privacy Policy</Link>
            </div>
          </div>
        </Container>
      </div>
    </footer>
  );
};

export default Footer;
